import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type ClienteResumen = {
  cliente?: string;
  semanal?: number | string;
};

type ChoferResumen = {
  chofer?: string;
  semanal?: number | string;
  parcial?: number | string;
  "%": number | string;
};

type AdminResponse = {
  resumen_clientes?: ClienteResumen[];
  resumen_cadetes?: ChoferResumen[];
};

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionsStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const tabsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  minHeight: "40px",
  padding: "0 16px",
  borderRadius: "999px",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  background: active ? "var(--primary-soft)" : "transparent",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  fontSize: "14px",
  fontWeight: active ? 700 : 600,
  display: "inline-flex",
  alignItems: "center",
});

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "30px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const analyticsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.6fr 1fr",
  gap: "20px",
};

const chartBlockStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionSubtextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: "14px",
  color: "var(--text-secondary)",
};

const stackedSideStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateRows: "auto auto",
  gap: "20px",
};

const miniStatsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "12px",
  marginTop: "16px",
};

const miniStatStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
};

const numberFormatter = (value: number) =>
  new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 0,
  }).format(value);

const currencyFormatter = (value: number) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value);

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const normalized = value.replace(/\./g, "").replace(",", ".");
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value?: number | string; name?: string; color?: string }>;
  label?: string;
}) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      style={{
        background: "var(--surface-alt)",
        border: "1px solid var(--border)",
        borderRadius: "16px",
        padding: "12px 14px",
        boxShadow: "var(--shadow)",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          fontWeight: 700,
          color: "var(--text-primary)",
          marginBottom: "8px",
        }}
      >
        {label}
      </div>

      {payload.map((entry, index) => (
        <div
          key={`${entry.name}-${index}`}
          style={{
            fontSize: "13px",
            color: "var(--text-secondary)",
            marginTop: "4px",
          }}
        >
          {entry.name}:{" "}
          <span style={{ color: "var(--text-primary)", fontWeight: 700 }}>
            {numberFormatter(toNumber(entry.value))}
          </span>
        </div>
      ))}
    </div>
  );
};

const HomeAdmin = () => {
  const { token } = useAuth();

  const [clientes, setClientes] = useState<ClienteResumen[]>([]);
  const [choferes, setChoferes] = useState<ChoferResumen[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const response = await authFetch("/v1/data/admin", { token });
        const data: AdminResponse = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error("Error al cargar resumen admin");
        }

        setClientes(data.resumen_clientes || []);
        setChoferes(data.resumen_cadetes || []);
      } catch (error) {
        console.error("Error al cargar resumen admin:", error);
        setClientes([]);
        setChoferes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [token]);

  const totalSemanalClientes = useMemo(() => {
    return clientes.reduce((acc, item) => acc + toNumber(item.semanal), 0);
  }, [clientes]);

  const totalSemanalChoferes = useMemo(() => {
    return choferes.reduce((acc, item) => acc + toNumber(item.semanal), 0);
  }, [choferes]);

  const totalParcialChoferes = useMemo(() => {
    return choferes.reduce((acc, item) => acc + toNumber(item.parcial), 0);
  }, [choferes]);

  const promedioPorChofer = useMemo(() => {
    if (!choferes.length) return 0;
    return Math.round(totalSemanalChoferes / choferes.length);
  }, [choferes, totalSemanalChoferes]);

  const clientesChartData = useMemo(() => {
    return clientes
      .slice(0, 8)
      .map((item) => ({
        name: item.cliente || "Sin nombre",
        semanal: toNumber(item.semanal),
      }));
  }, [clientes]);

  const choferesLineData = useMemo(() => {
    return choferes
      .slice(0, 8)
      .map((item) => ({
        name: item.chofer || "Chofer",
        semanal: toNumber(item.semanal),
        parcial: toNumber(item.parcial),
      }));
  }, [choferes]);

  const topChoferes = useMemo(() => {
    return [...choferes]
      .sort((a, b) => toNumber(b.semanal) - toNumber(a.semanal))
      .slice(0, 6);
  }, [choferes]);

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Dashboard</h1>
          <p style={subtitleStyle}>
            Vista general de operación, volumen por tienda y desempeño de choferes.
          </p>
        </div>

        <div style={actionsStyle}>
          <Button variant="secondary">Últimos 30 días</Button>
          <Button>Exportar</Button>
        </div>
      </div>

      <div style={tabsRowStyle}>
        <div style={tabStyle(true)}>Resumen</div>
        <div style={tabStyle(false)}>Operación</div>
        <div style={tabStyle(false)}>Tiendas</div>
        <div style={tabStyle(false)}>Choferes</div>
        <div style={tabStyle(false)}>Finanzas</div>
      </div>

      <div style={kpiGridStyle} className="homeadmin-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Tiendas activas</div>
          <div style={kpiValueStyle}>{clientes.length}</div>
          <div style={kpiMetaStyle}>Cantidad de tiendas con resumen disponible.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Choferes activos</div>
          <div style={kpiValueStyle}>{choferes.length}</div>
          <div style={kpiMetaStyle}>Choferes con volumen informado en el período.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Total semanal tiendas</div>
          <div style={kpiValueStyle}>{currencyFormatter(totalSemanalClientes)}</div>
          <div style={kpiMetaStyle}>Suma del campo semanal de tiendas.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Promedio por chofer</div>
          <div style={kpiValueStyle}>{currencyFormatter(promedioPorChofer)}</div>
          <div style={kpiMetaStyle}>Promedio semanal estimado por conductor.</div>
        </Card>
      </div>

      <div style={analyticsGridStyle} className="homeadmin-analytics-grid">
        <div style={chartBlockStyle}>
          <Card>
            <div style={{ marginBottom: "18px" }}>
              <h2 style={sectionTitleStyle}>Volumen semanal por tienda</h2>
              <p style={sectionSubtextStyle}>
                Resumen comparativo de las principales tiendas del período.
              </p>
            </div>

            <div style={{ width: "100%", height: "340px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={clientesChartData}>
                  <CartesianGrid stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "currentColor", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "currentColor", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="semanal" fill="var(--primary)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card>
            <div style={{ marginBottom: "18px" }}>
              <h2 style={sectionTitleStyle}>Comparativa choferes</h2>
              <p style={sectionSubtextStyle}>
                Relación entre total semanal y parcial por conductor.
              </p>
            </div>

            <div style={{ width: "100%", height: "320px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={choferesLineData}>
                  <CartesianGrid stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "currentColor", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "currentColor", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="semanal"
                    stroke="var(--primary)"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Semanal"
                  />
                  <Line
                    type="monotone"
                    dataKey="parcial"
                    stroke="var(--success)"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Parcial"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <div style={stackedSideStyle}>
          <Card>
            <div>
              <h2 style={sectionTitleStyle}>Resumen operativo</h2>
              <p style={sectionSubtextStyle}>
                Indicadores rápidos para lectura ejecutiva.
              </p>
            </div>

            <div style={miniStatsGridStyle}>
              <div style={miniStatStyle}>
                <div style={kpiLabelStyle}>Total semanal choferes</div>
                <div style={{ ...kpiValueStyle, fontSize: "24px" }}>
                  {currencyFormatter(totalSemanalChoferes)}
                </div>
              </div>

              <div style={miniStatStyle}>
                <div style={kpiLabelStyle}>Total parcial choferes</div>
                <div style={{ ...kpiValueStyle, fontSize: "24px" }}>
                  {currencyFormatter(totalParcialChoferes)}
                </div>
              </div>

              <div style={miniStatStyle}>
                <div style={kpiLabelStyle}>Top tiendas</div>
                <div style={{ ...kpiValueStyle, fontSize: "24px" }}>
                  {clientesChartData.length}
                </div>
              </div>

              <div style={miniStatStyle}>
                <div style={kpiLabelStyle}>Top choferes</div>
                <div style={{ ...kpiValueStyle, fontSize: "24px" }}>
                  {topChoferes.length}
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div style={{ marginBottom: "12px" }}>
              <h2 style={sectionTitleStyle}>Choferes destacados</h2>
              <p style={sectionSubtextStyle}>
                Ordenados por total semanal informado.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {topChoferes.length === 0 && (
                <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                  {loading ? "Cargando datos..." : "No hay datos disponibles."}
                </div>
              )}

              {topChoferes.map((chofer, index) => (
                <div
                  key={`${chofer.chofer || "chofer"}-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "center",
                    padding: "12px 14px",
                    border: "1px solid var(--border)",
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "var(--text-primary)",
                      }}
                    >
                      {chofer.chofer || "Sin nombre"}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--text-secondary)",
                        marginTop: "4px",
                      }}
                    >
                      Parcial: {currencyFormatter(toNumber(chofer.parcial))}
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "var(--text-primary)",
                    }}
                  >
                    {currencyFormatter(toNumber(chofer.semanal))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Card>
        <div style={{ marginBottom: "14px" }}>
          <h2 style={sectionTitleStyle}>Detalle por chofer</h2>
          <p style={sectionSubtextStyle}>
            Tabla resumida con total semanal, porcentaje y parcial.
          </p>
        </div>

        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Chofer</th>
                <th style={thStyle}>Total semanal</th>
                <th style={thStyle}>%</th>
                <th style={thStyle}>Parcial</th>
              </tr>
            </thead>

            <tbody>
              {choferes.length === 0 && (
                <tr>
                  <td style={tdStyle} colSpan={4}>
                    {loading ? "Cargando datos..." : "No hay información disponible."}
                  </td>
                </tr>
              )}

              {choferes.map((ch, index) => (
                <tr key={`${ch.chofer || "chofer"}-${index}`}>
                  <td style={tdStyle}>{ch.chofer || "-"}</td>
                  <td style={tdStyle}>{currencyFormatter(toNumber(ch.semanal))}</td>
                  <td style={tdStyle}>{String(ch["%"] ?? "-")}</td>
                  <td style={tdStyle}>{currencyFormatter(toNumber(ch.parcial))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <style>
        {`
          .homeadmin-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .homeadmin-analytics-grid {
            grid-template-columns: 1.6fr 1fr;
          }

          @media (max-width: 1180px) {
            .homeadmin-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .homeadmin-analytics-grid {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 720px) {
            .homeadmin-kpi-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default HomeAdmin;